// Normal-viewer adapter for the shared metre-scale Earth renderers.
// No career, vessels, HUD, time warp or rocketry lifecycle is created here.
import * as THREE from 'three';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { getEarthImageryStream, earthImageryUniforms, earthOceanMaskTexture } from './rocketry/earthimagery.js';
import { createVolumetricClouds, GLOBAL_VOLUME_FADE } from './rocketry/volclouds.js';
import { PREPASS_VERT, PREPASS_FRAG } from './rocketry/cloudshaders.js';
import { EventBus } from './rocketry/events.js';
import { QUALITY_TIERS } from './rocketry/settings.js';
import { approximateSkyColor, SkyDome } from './rocketry/sky.js';
import { updateEclipseSky, updateGiantSky } from './eclipse/totality.js';
import { RUNNING_ON_MOBILE } from './mobiledevice.js';

export const EARTH_METRES = 6371000;
export const EARTH_ELEVATION_METRES = 8848;
export const EARTH_INSPECTION_CLEARANCE = 2000;
// The air close in: rocketry's scattering sky dome (sky.js) draws the
// atmosphere below this altitude band (metres) and the globe's exterior shell
// hands over to it across the band, so low orbit shows a thin blue limb
// fading to black, the orbital sunset and the night airglow, while the
// default whole-Earth view (about 25,000 km up) keeps the validated shell.
export const VIEWER_SKY_HANDOVER = [3000e3, 8000e3];
// How far the air bends a ray from space that grazes the ground and leaves
// again, in radians: the refractivity of air at the ground, about 2.9e-4,
// over the same grazing path as its extinction (Chapman's sqrt(2 pi R / H),
// 70.7 for H = 8 km), so 0.0205, 70 arcminutes, twice the 35 a horizon
// lifts the Sun by from the ground. A ray grazing at tangent height h bends
// by this times e^(-h / 8 km), which lifts a Sun near the limb seen from
// orbit and squashes it flat (the dome's disc, the glare's core and the
// light the glare measures in main.js).
export const AIR_GRAZING_BEND = 2.9e-4 * 70.7;

// Image-row UVs match SphereGeometry and Texture.flipY, including the seam.
export function earthSurfaceUV(direction, out = new THREE.Vector2()) {
  return out.set(THREE.MathUtils.euclideanModulo(Math.atan2(direction.z, -direction.x) / (2 * Math.PI), 1),
    THREE.MathUtils.clamp(0.5 - Math.asin(THREE.MathUtils.clamp(direction.y, -1, 1)) / Math.PI, 0, 1));
}

// The inverse: the unit direction in the Earth mesh's own frame under a
// latitude and an east longitude in degrees (north is +Y, longitude 0 is +X,
// east is atan2(-z, x)), so earthSurfaceUV(earthSurfaceDirection(lat, lon))
// reads the texel at that place.
export function earthSurfaceDirection(lat, lon, out = new THREE.Vector3()) {
  const phi = THREE.MathUtils.degToRad(lat);
  const lam = THREE.MathUtils.degToRad(lon);
  return out.set(Math.cos(phi) * Math.cos(lam), Math.sin(phi), -Math.cos(phi) * Math.sin(lam));
}

// Full inverse transform includes daily spin, axial tilt and either display
// scale. Tile addresses and clouds consequently share geographic coordinates.
export function earthCameraFrame(mesh, camera, position, orientation) {
  mesh.updateWorldMatrix(true, false);
  camera.updateWorldMatrix(true, false);
  position.copy(camera.position).applyMatrix4(new THREE.Matrix4().copy(mesh.matrixWorld).invert());
  position.multiplyScalar(EARTH_METRES / mesh.geometry.parameters.radius);
  mesh.getWorldQuaternion(orientation).invert().multiply(camera.getWorldQuaternion(new THREE.Quaternion()));
}

export function createEarthCloseView(ctx) {
  const imagery = getEarthImageryStream(ctx.renderer);
  // The stream's stats for checks from the console or a test
  // (scripts/test-earth-tiles.mjs).
  if (typeof window !== 'undefined') window.__earthImagery = imagery;
  const position = new THREE.Vector3(), orientation = new THREE.Quaternion();
  const inverse = new THREE.Matrix4(), sunFixed = new THREE.Vector3();
  const eclSun = new THREE.Vector3(), eclMoon = new THREE.Vector3();
  // The eclipse as the dome last drew it (src/eclipse/totality.js): visible
  // is the share of the Sun's disc showing from the camera.
  const eclipse = { on: false, disc: false, visible: 1 };
  let skyIsWanted = false;
  const R = { active: false, mode: 'viewer', viewBody: 'earth', mapMode: false, patches: new Map() };
  const events = new EventBus();
  let cloudSystem = null, G = null, pass = null, attachedComposer = null, depthMesh = null, sky = null, shell = null;
  let occluder = null;
  const occluderProxies = [], occluderMatrix = new THREE.Matrix4();
  const proxyMaterial = new THREE.MeshBasicMaterial();
  // The sky dome is composited over the whole scaled scene, so the models in
  // front of the air must keep it off: the station, satellites with models
  // and the fly ship write depth into this pass first (skyDepthMaterial) and
  // the dome, drawn at the far plane, fails the depth test behind them. The
  // aurora glows above the dense air and is left in the scaled scene: at
  // night the dome adds almost no light and only dims rays that graze the
  // dense air, so the curtains composite as they did.
  const skyDepthMaterial = new THREE.MeshBasicMaterial({ colorWrite: false });
  const skyOccluders = [], skyMatrix = new THREE.Matrix4(), skyScale = new THREE.Matrix4();
  let skyOccluderKey = '', skyKeyFrame = 0;
  let streaming = false, lastQuality = '', altitude = Infinity;
  // The volume's wind runs on the clock the globe's clouds run on: the
  // globe's weatherTime (earthweather.js: the animation clock in seconds,
  // carried smoothly between the weather field's renders). The volume
  // turns off and on again with a climb past 200 km, a focus tween or a
  // preset change, and resyncing it to astronomical time there (which runs
  // up to 25,000 times faster than the animation clock) moved the whole
  // field on every return. On one clock, zooming out and back in finds the
  // clouds the wind has carried along, and nothing else.
  const weatherEpoch = ctx.getSimulationJD();
  let weatherJD = weatherEpoch;
  function weatherSeconds(mesh, now) {
    const globe = mesh.material.uniforms.weatherTime?.value;
    return Number.isFinite(globe) ? globe : now / 1000;
  }

  function tier() { return QUALITY_TIERS[ctx.getQuality()] || QUALITY_TIERS.high; }

  // The true-scale local pass (metres, camera at the origin, axes of the
  // Earth's own frame): the sky dome, and the volumetric clouds when wanted.
  function makeLocal(mesh) {
    const localScene = new THREE.Scene();
    const localCamera = new THREE.PerspectiveCamera(60, 1, 1, 2e7);
    localScene.add(localCamera);
    // Reuse the real Earth's geometry AND vertex displacement in the cloud
    // depth pass, so mountains correctly hide cloud, and coastlines agree.
    const depthUniforms = { bumpTexture: mesh.material.uniforms.bumpTexture,
      displacementScale: mesh.material.uniforms.displacementScale,
      terrainDepth: { value: 1 } };
    const depthMaterial = new THREE.ShaderMaterial({
      uniforms: depthUniforms,
      vertexShader: 'uniform sampler2D bumpTexture; uniform float displacementScale; uniform float terrainDepth;\n' + PREPASS_VERT.replace('#include <begin_vertex>',
        'vec3 transformed = position + normalize(position) * texture2D(bumpTexture, uv).r * displacementScale * terrainDepth;'),
      fragmentShader: PREPASS_FRAG
    });
    depthMesh = new THREE.Mesh(mesh.geometry, new THREE.MeshBasicMaterial());
    depthMesh.onBeforeRender = () => { depthUniforms.terrainDepth.value = 1; depthMaterial.uniformsNeedUpdate = true; };
    depthMesh.layers.set(7); // Depth only; the main pass already draws Earth.
    depthMesh.frustumCulled = false;
    localScene.add(depthMesh);
    const sunLight = new THREE.DirectionalLight(0xfff4e6, 2.6);
    const hemi = new THREE.HemisphereLight(0x8fb8ff, 0x3a2f22, 0.15);
    const env = { sunLight, hemi, state: { sunDir: sunFixed, keyDir: sunFixed, keyIsMoon: false, dayLight: 1, hazeScaleHeight: 2000 } };
    localScene.fog = new THREE.Fog(0xbfd0e6, 0, 22);
    const cloudContext = Object.create(ctx);
    cloudContext.getSimulationJD = () => weatherJD;
    G = { ctx: cloudContext, R, events, localScene, localCamera, camInertial: position, env,
      bodies: { get: () => ({ radius: EARTH_METRES, spin: 0 }) },
      settings: { tier }, globalClouds: true, cloudPrepassMaterial: depthMaterial };
    sky = new SkyDome({ eclipse: true });
    sky.setBody({ key: 'earth', radius: EARTH_METRES, atmosphereHeight: 100000, scaleHeight: 8000 });
    // Ground rays: the globe draws its own haze over the disc at every
    // height (earth.js, calibrated against DSCOVR EPIC and scaled by the air
    // below the camera), so away from the horizon the dome adds nothing; it
    // keeps its full column only on rays grazing the last 40 km of the
    // planet's radius, which is the limb's white base from orbit and the
    // haze the far sea fades into from inside the air. With rocketry's 0.55
    // from space and its full column inside the air, the view straight down
    // from 30 km came out twice as bright.
    sky.uniforms.uGroundHaze.value = 0;
    sky.uniforms.uGroundOverGlobe.value = 1;
    // Ozone at a fifth: in single scattering the full Chappuis absorption
    // along the 1,000 km tangent path leaves red and blue, a lavender band
    // with a pink line at its base, where ISS photographs show white haze
    // under azure. A fifth still warms the orbital sunset band to orange.
    sky.uniforms.uBetaO.value.multiplyScalar(0.2);
    // Airglow as photographed from the station: a faint yellow-green line.
    sky.uniforms.uAirglowColor.value.set(0.75, 1.0, 0.18);
    // The viewer's stars are dimmed for a sunlit view in main.js (star
    // layers only), so the dome's black veil over space stays off and the
    // Moon and the Sun behind it are not dimmed with them.
    sky.uniforms.uStarDim.value = 0;
    sky.uniforms.uRefract.value = AIR_GRAZING_BEND;
    localScene.add(sky.mesh);
    pass = new RenderPass(localScene, localCamera);
    pass.clear = false; pass.clearDepth = true; pass.enabled = false;
  }

  function updateCloudOccluder(mesh) {
    const root = ctx.getCloudOccluder?.() || null;
    if (root !== occluder) {
      for (const {proxy} of occluderProxies) G.localScene.remove(proxy);
      occluderProxies.length = 0;
      occluder = root;
      root?.traverse((source) => {
        if (!source.isMesh || !source.geometry) return;
        const materials = Array.isArray(source.material) ? source.material : [source.material];
        if (!materials.some(m => m && !m.transparent && m.depthWrite)) return;
        const proxy = source.isInstancedMesh ? source.clone(false) : new THREE.Mesh(source.geometry, proxyMaterial);
        proxy.material = proxyMaterial;
        proxy.layers.set(7); proxy.frustumCulled = false; proxy.matrixAutoUpdate = false;
        proxy.onBeforeRender = () => {
          G.cloudPrepassMaterial.uniforms.terrainDepth.value = 0;
          G.cloudPrepassMaterial.uniformsNeedUpdate = true;
        };
        G.localScene.add(proxy); occluderProxies.push({source,proxy});
      });
    }
    if (!root) return;
    root.updateWorldMatrix(true,true);
    const scale = EARTH_METRES / mesh.geometry.parameters.radius;
    occluderMatrix.makeTranslation(-position.x,-position.y,-position.z)
      .multiply(new THREE.Matrix4().makeScale(scale,scale,scale))
      .multiply(inverse.copy(mesh.matrixWorld).invert());
    for (const {source,proxy} of occluderProxies) {
      let visible = true;
      for (let p=source;p;p=p.parent) if (!p.visible) { visible=false; break; }
      proxy.visible = visible;
      proxy.matrix.multiplyMatrices(occluderMatrix,source.matrixWorld);
      proxy.matrixWorldNeedsUpdate = true;
    }
  }

  // Depth proxies of the scaled scene's models in this pass's metres: the same
  // world transform carried into the Earth's frame and scaled, as the cloud
  // prepass occluders are placed.
  function chainVisible(o) { for (let p = o; p; p = p.parent) if (!p.visible) return false; return true; }
  function updateSkyLayers(mesh, on) {
    const occRoots = on ? (ctx.getSkyOccluders?.() || []).filter(Boolean) : [];
    // Models load after their roots exist, so the mesh count is part of the
    // key; it is recounted once a second rather than every frame.
    let key = occRoots.map((r) => r.uuid).join();
    if (++skyKeyFrame % 60 === 0 || key !== skyOccluderKey.split('#')[0]) {
      let meshes = 0;
      for (const r of occRoots) r.traverse((o) => { if (o.isMesh) meshes++; });
      key += '#' + meshes;
    } else key = skyOccluderKey;
    if (key !== skyOccluderKey) {
      for (const { proxy } of skyOccluders) G.localScene.remove(proxy);
      skyOccluders.length = 0;
      skyOccluderKey = key;
      for (const root of occRoots) root.traverse((source) => {
        if (!source.isMesh || !source.geometry) return;
        const materials = Array.isArray(source.material) ? source.material : [source.material];
        if (!materials.some((m) => m && !m.transparent && m.depthWrite !== false)) return;
        const proxy = source.isInstancedMesh ? source.clone(false) : new THREE.Mesh(source.geometry, skyDepthMaterial);
        proxy.material = skyDepthMaterial;
        proxy.renderOrder = -200; proxy.frustumCulled = false; proxy.matrixAutoUpdate = false;
        G.localScene.add(proxy); skyOccluders.push({ source, proxy });
      });
    }
    if (!skyOccluders.length) return;
    const scale = EARTH_METRES / mesh.geometry.parameters.radius;
    skyMatrix.makeTranslation(-position.x, -position.y, -position.z)
      .multiply(skyScale.makeScale(scale, scale, scale))
      .multiply(inverse.copy(mesh.matrixWorld).invert());
    for (const { source, proxy } of skyOccluders) {
      source.updateWorldMatrix(true, false);
      proxy.visible = chainVisible(source);
      proxy.matrix.multiplyMatrices(skyMatrix, source.matrixWorld);
      proxy.matrixWorldNeedsUpdate = true;
    }
  }
  function releaseSkyLayers() {
    for (const { proxy } of skyOccluders) G?.localScene.remove(proxy);
    skyOccluders.length = 0; skyOccluderKey = '';
  }

  // The exterior shell, faded where the dome takes over.
  function setShellFade(mesh, f) {
    if (!shell || shell.userData.surface !== mesh) {
      let root = mesh;
      while (root && root.parent && !root.parent.isScene) root = root.parent;
      shell = root ? root.getObjectByName('atmosphere') : null;
      if (shell) shell.userData.surface = mesh;
    }
    const u = shell?.material?.uniforms?.uShellCloseFade;
    if (u) u.value = f;
  }

  // The Sun and the Moon in the dome's frame (the Earth's own axes, km from
  // its centre), carried in by the inverse of the mesh's world matrix as the
  // camera is, so the shadow lands where the scene puts it.
  function updateEclipse(mesh, now) {
    // The far future's red giant (?whatif=future, src/redgiant.js) is drawn
    // by the dome too, in place of the Sun's meshes.
    const giant = skyIsWanted ? ctx.getGiant?.() : null;
    const bodies = skyIsWanted && !giant ? ctx.getEclipseBodies?.() : null;
    let state = { on: false, disc: false, visible: 1 };
    if (giant) {
      const k = EARTH_METRES / mesh.geometry.parameters.radius / 1000;
      eclSun.copy(giant.sun).applyMatrix4(inverse).multiplyScalar(k);
      updateGiantSky(sky.uniforms, {
        cam: [position.x / 1000, position.y / 1000, position.z / 1000],
        sun: [eclSun.x, eclSun.y, eclSun.z], sunR: giant.sunR, time: now / 1000,
      });
      state = { on: false, disc: true, visible: 1 };
    } else if (bodies && bodies.moon) {
      const k = EARTH_METRES / mesh.geometry.parameters.radius / 1000;
      eclSun.copy(bodies.sun).applyMatrix4(inverse).multiplyScalar(k);
      eclMoon.copy(bodies.moon).applyMatrix4(inverse).multiplyScalar(k);
      state = updateEclipseSky(sky.uniforms, {
        cam: [position.x / 1000, position.y / 1000, position.z / 1000],
        sun: [eclSun.x, eclSun.y, eclSun.z], moon: [eclMoon.x, eclMoon.y, eclMoon.z],
        sunR: bodies.sunR, moonR: bodies.moonR, time: now / 1000,
        limbRot: bodies.limbRot || 0,
      });
    } else {
      sky.uniforms.uEclOn.value = 0;
      sky.uniforms.uEclDisc.value = 0;
      sky.uniforms.uGiant.value = 0;
    }
    // The dome draws the Sun itself while the Moon is near it.
    sky.uniforms.uSunDisc.value = state.disc ? 0 : 60;
    if (state.disc !== eclipse.disc) ctx.setSunDrawnBySky?.(state.disc);
    Object.assign(eclipse, state);
  }

  function stop() {
    if (eclipse.disc) ctx.setSunDrawnBySky?.(false);
    Object.assign(eclipse, { on: false, disc: false, visible: 1 });
    if (streaming) { imagery.cancel(); streaming = false; }
    if (R.active) { R.active = false; events.emit('exit', {}); }
    if (pass) pass.enabled = false;
    const u = shell?.material?.uniforms?.uShellCloseFade;
    if (u) u.value = 0;
    releaseSkyLayers();
    earthImageryUniforms.uEarthVolumeBlend.value = 0;
    altitude = Infinity;
  }

  function update(now) {
    const mesh = ctx.getEarthMesh();
    if (ctx.suspended() || !mesh?.material?.uniforms?.dayTexture || ctx.renderer.xr.isPresenting) { stop(); return; }
    earthCameraFrame(mesh, ctx.camera, position, orientation);
    altitude = position.length() - EARTH_METRES;
    // Only request satellite pages where the bundled globe needs refinement.
    // A phone frees the atlas once the Earth is a small disc (8 radii up),
    // where nothing it holds is drawn (docs/mobile-memory.md).
    if (altitude > EARTH_METRES * 4 || altitude < -20000) {
      stop();
      if (RUNNING_ON_MOBILE && altitude > EARTH_METRES * 8) imagery.release();
      return;
    }
    streaming = true;
    const quality = ctx.getQuality();
    imagery.update({ now, position, orientation, lens: ctx.camera.projectionMatrix,
      oceanMask: earthOceanMaskTexture(mesh.material.uniforms), budget: tier().earthTiles });
    const cloudWanted = ctx.isEarthFocused() && !ctx.cloudsHeld?.() && tier().volClouds && altitude < GLOBAL_VOLUME_FADE[1];
    // The dome over the hand-over band; the shell fades the other way.
    const skyFade = 1 - THREE.MathUtils.smoothstep(altitude, VIEWER_SKY_HANDOVER[0], VIEWER_SKY_HANDOVER[1]);
    // window.__viewerSkyOff = true: the shell alone, for before/after captures.
    const skyWanted = skyFade > 0.002 && !(typeof window !== 'undefined' && window.__viewerSkyOff);
    skyIsWanted = skyWanted;
    setShellFade(mesh, skyWanted ? skyFade : 0);
    if (!cloudWanted && !skyWanted) {
      if (R.active) { R.active = false; events.emit('exit', {}); }
      if (pass) pass.enabled = false;
      if (G) releaseSkyLayers();
      earthImageryUniforms.uEarthVolumeBlend.value = 0;
      return;
    }
    if (!G) makeLocal(mesh);
    if (cloudWanted && !cloudSystem) cloudSystem = createVolumetricClouds(G);
    if (attachedComposer !== ctx.composer) {
      attachedComposer?.removePass(pass);
      attachedComposer = ctx.composer;
      attachedComposer.insertPass(pass, attachedComposer.passes.indexOf(ctx.composerRenderPass) + 1);
    }
    if (quality !== lastQuality) {
      if (cloudSystem) events.emit('settings', {key:'quality'});
      sky.setQuality(tier());
      lastQuality = quality;
    }
    weatherJD = weatherEpoch + weatherSeconds(mesh, now) / 86400;
    pass.enabled = true;
    const camera = G.localCamera;
    camera.far = Math.max(2e7, position.length() + EARTH_METRES * 2);
    camera.position.set(0, 0, 0);
    camera.quaternion.copy(orientation);
    camera.fov = ctx.camera.fov; camera.aspect = ctx.camera.aspect; camera.zoom = ctx.camera.zoom;
    camera.projectionMatrix.copy(ctx.camera.projectionMatrix);
    const p = camera.projectionMatrix.elements;
    p[10] = -(camera.far + camera.near) / (camera.far - camera.near);
    p[14] = -2 * camera.far * camera.near / (camera.far - camera.near);
    camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
    camera.updateMatrixWorld(true);
    depthMesh.geometry = mesh.geometry;
    depthMesh.scale.setScalar(EARTH_METRES / mesh.geometry.parameters.radius);
    depthMesh.position.copy(position).negate();
    G.cloudPrepassMaterial.uniforms.bumpTexture = mesh.material.uniforms.bumpTexture;
    G.cloudPrepassMaterial.uniforms.displacementScale = mesh.material.uniforms.displacementScale;
    depthMesh.updateMatrixWorld(true);
    inverse.copy(mesh.matrixWorld).invert();
    sunFixed.copy(ctx.getSunPosition()).applyMatrix4(inverse).normalize();
    const sunMu = sunFixed.dot(position) / position.length();
    // The dome, in the Earth's frame in metres like the rest of this pass.
    sky.update(position, sunFixed, camera.position);
    sky.uniforms.uFade.value = skyFade;
    sky.uniforms.uStarDim.value = 0;
    sky.mesh.visible = skyWanted;
    // Models in front of the air hide the dome.
    sky.mesh.material.depthTest = true;
    updateEclipse(mesh, now);
    updateSkyLayers(mesh, skyWanted);
    if (!cloudWanted) {
      if (R.active) { R.active = false; events.emit('exit', {}); }
      earthImageryUniforms.uEarthVolumeBlend.value = 0;
      return;
    }
    R.active = true;
    G.env.state.dayLight = THREE.MathUtils.smoothstep(sunMu, -0.08, 0.15);
    approximateSkyColor(0, sunMu, G.localScene.fog.color);
    G.localScene.fog.near = Math.max(altitude, 0);
    updateCloudOccluder(mesh);
    G.volClouds.uniforms.uCloudDeckOpacity.value = mesh.material.uniforms.cloudOpacity.value;
    events.emit('frame', { now });
    earthImageryUniforms.uEarthVolumeBlend.value = G.volClouds.stats().active ? G.volClouds.uniforms.uOpacity.value : 0;
  }

  return { update, stop, get active() { return R.active; },
    get eclipse() { return eclipse; },
    stats: () => ({ altitude, streaming, sunElevation: sunFixed.dot(position) / position.length(), clouds: G?.volClouds?.stats() || null, imagery: {...imagery.stats} }),
    get clouds() { return G?.volClouds; },
    // The low-orbit sky dome (rocketry sky.js), for checks and captures.
    get sky() { return sky; },
    dispose() { stop(); cloudSystem?.dispose(); sky?.dispose(); proxyMaterial.dispose(); skyDepthMaterial.dispose(); attachedComposer?.removePass(pass); depthMesh?.material.dispose(); } };
}
